import React, { useState, useMemo, useCallback } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X, Search, AlertTriangle } from 'lucide-react';

interface DateRange {
  fromDate: string | null;
  toDate: string | null;
}

interface DateRangePickerProps {
  value: DateRange;
  onSubmit: (range: DateRange) => void;
  disabled?: boolean;
  minYear?: number;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onSubmit,
  disabled = false,
  minYear = 2025
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeField, setActiveField] = useState<'from' | 'to'>('from');
  const [tempFromDate, setTempFromDate] = useState<string | null>(value.fromDate);
  const [tempToDate, setTempToDate] = useState<string | null>(value.toDate);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentDate = new Date().getDate();

  // Parse active date
  const activeDate = activeField === 'from' ? tempFromDate : tempToDate;
  const parsedActive = activeDate ? new Date(activeDate) : new Date();

  const [viewYear, setViewYear] = useState(parsedActive.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsedActive.getMonth() + 1);

  // Generate years (from minYear to current year)
  const years = useMemo(() => {
    const yrs = [];
    for (let y = currentYear; y >= minYear; y--) {
      yrs.push(y);
    }
    return yrs;
  }, [currentYear, minYear]);

  // Get days in month
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  // Get day of week for first day of month (0 = Sunday)
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month - 1, 1).getDay();
  };

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const days: (number | null)[] = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      days.push(d);
    }

    return days;
  }, [viewYear, viewMonth]);

  // Format date for display
  const formatDisplayDate = (dateStr: string | null) => {
    if (!dateStr) return 'Select date';
    const date = new Date(dateStr);
    return `${date.getDate()} ${SHORT_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Format date as YYYY-MM-DD
  const formatDateString = (year: number, month: number, day: number) => {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Check if date is in range
  const isInRange = (day: number) => {
    if (!tempFromDate || !tempToDate) return false;
    const dateStr = formatDateString(viewYear, viewMonth, day);
    return dateStr >= tempFromDate && dateStr <= tempToDate;
  };

  const isFromDate = (day: number) => {
    if (!tempFromDate) return false;
    return formatDateString(viewYear, viewMonth, day) === tempFromDate;
  };

  const isToDate = (day: number) => {
    if (!tempToDate) return false;
    return formatDateString(viewYear, viewMonth, day) === tempToDate;
  };

  const isToday = (day: number) => {
    return viewYear === currentYear && viewMonth === currentMonth && day === currentDate;
  };

  const isFuture = (day: number) => {
    if (viewYear > currentYear) return true;
    if (viewYear === currentYear && viewMonth > currentMonth) return true;
    if (viewYear === currentYear && viewMonth === currentMonth && day > currentDate) return true;
    return false;
  };



  // Calculate months difference between two dates
  const getMonthsDifference = (from: string, to: string): number => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const months = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    return Math.abs(months);
  };

  // Handle date selection
  const handleDateSelect = (day: number) => {
    if (isFuture(day)) return;

    const dateStr = formatDateString(viewYear, viewMonth, day);

    if (activeField === 'from') {
      setTempFromDate(dateStr);
      // If toDate is before fromDate, reset toDate
      if (tempToDate && dateStr > tempToDate) {
        setTempToDate(null);
      }
      setActiveField('to');
    } else {
      // Ensure toDate is not before fromDate
      if (tempFromDate && dateStr < tempFromDate) {
        setTempToDate(tempFromDate);
        setTempFromDate(dateStr);
      } else {
        setTempToDate(dateStr);
      }
    }
  };

  // Handle submit
  const handleSubmit = useCallback(() => {
    if (!tempFromDate || !tempToDate) {
      alert('Please select both From and To dates');
      return;
    }

    // Check if range is more than 3 months
    const monthsDiff = getMonthsDifference(tempFromDate, tempToDate);
    if (monthsDiff > 3) {
      setShowConfirmDialog(true);
      return;
    }

    onSubmit({ fromDate: tempFromDate, toDate: tempToDate });
    setIsOpen(false);
  }, [tempFromDate, tempToDate, onSubmit]);

  // Confirm large range
  const confirmLargeRange = () => {
    if (tempFromDate && tempToDate) {
      onSubmit({ fromDate: tempFromDate, toDate: tempToDate });
      setShowConfirmDialog(false);
      setIsOpen(false);
    }
  };

  // Clear dates
  const clearDates = () => {
    setTempFromDate(null);
    setTempToDate(null);
    setActiveField('from');
  };

  // Navigate months
  const goToPrevMonth = () => {
    if (viewMonth === 1) {
      if (viewYear > minYear) {
        setViewMonth(12);
        setViewYear(viewYear - 1);
      }
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 12) {
      if (viewYear < currentYear) {
        setViewMonth(1);
        setViewYear(viewYear + 1);
      }
    } else if (!(viewYear === currentYear && viewMonth >= currentMonth)) {
      setViewMonth(viewMonth + 1);
    }
  };

  // Display value for button
  const displayValue = useMemo(() => {
    if (value.fromDate && value.toDate) {
      if (value.fromDate === value.toDate) {
        return formatDisplayDate(value.fromDate);
      }
      return `${formatDisplayDate(value.fromDate)} - ${formatDisplayDate(value.toDate)}`;
    }
    return 'Select date range';
  }, [value]);

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Calendar className="w-4 h-4 text-gray-500" />
        <span className="max-w-[200px] truncate">{displayValue}</span>
        {(value.fromDate || value.toDate) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSubmit({ fromDate: null, toDate: null });
            }}
            className="hover:bg-gray-100 rounded p-0.5"
          >
            <X className="w-3 h-3 text-gray-400" />
          </button>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 p-4 min-w-[340px] right-0">
          {/* From/To Selection */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setActiveField('from')}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${activeField === 'from'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
            >
              <div className="text-xs text-gray-500 mb-0.5">From</div>
              <div className={tempFromDate ? 'text-gray-900' : 'text-gray-400'}>
                {formatDisplayDate(tempFromDate)}
              </div>
            </button>
            <button
              onClick={() => setActiveField('to')}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${activeField === 'to'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
            >
              <div className="text-xs text-gray-500 mb-0.5">To</div>
              <div className={tempToDate ? 'text-gray-900' : 'text-gray-400'}>
                {formatDisplayDate(tempToDate)}
              </div>
            </button>
          </div>

          {/* Year Selector */}
          <div className="mb-3">
            <select
              value={viewYear}
              onChange={(e) => setViewYear(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={goToPrevMonth}
              disabled={viewYear === minYear && viewMonth === 1}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-semibold text-gray-900">
              {MONTHS[viewMonth - 1]} {viewYear}
            </span>
            <button
              onClick={goToNextMonth}
              disabled={viewYear === currentYear && viewMonth >= currentMonth}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Month Quick Select */}
          <div className="grid grid-cols-6 gap-1 mb-3">
            {SHORT_MONTHS.map((month, idx) => {
              const monthNum = idx + 1;
              const isFutureMonth = viewYear === currentYear && monthNum > currentMonth;
              return (
                <button
                  key={month}
                  onClick={() => !isFutureMonth && setViewMonth(monthNum)}
                  disabled={isFutureMonth}
                  className={`py-1 rounded text-xs font-medium transition-colors ${monthNum === viewMonth
                      ? 'bg-blue-500 text-white'
                      : isFutureMonth
                        ? 'opacity-40 cursor-not-allowed text-gray-400'
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                >
                  {month}
                </button>
              );
            })}
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
              <div key={idx} className="text-center text-xs font-medium text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {calendarDays.map((day, idx) => (
              <div key={idx} className="aspect-square">
                {day && (
                  <button
                    onClick={() => handleDateSelect(day)}
                    disabled={isFuture(day)}
                    className={`w-full h-full rounded-lg text-sm font-medium transition-colors ${isFromDate(day) || isToDate(day)
                        ? 'bg-blue-500 text-white'
                        : isInRange(day)
                          ? 'bg-blue-100 text-blue-700'
                          : isToday(day)
                            ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-300'
                            : isFuture(day)
                              ? 'opacity-40 cursor-not-allowed text-gray-300'
                              : 'hover:bg-gray-100 text-gray-700'
                      }`}
                  >
                    {day}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-3 border-t">
            <button
              onClick={clearDates}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Clear
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => setIsOpen(false)}
                className="px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!tempFromDate || !tempToDate}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Search className="w-4 h-4" />
                Fetch Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Large Range */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Large Date Range</h3>
                <p className="text-gray-600 text-sm mb-4">
                  You have selected a date range of more than 3 months. This may take longer to load and could affect performance.
                </p>
                <p className="text-gray-600 text-sm mb-4">
                  <strong>Selected range:</strong> {formatDisplayDate(tempFromDate)} to {formatDisplayDate(tempToDate)}
                </p>
                <p className="text-gray-600 text-sm">
                  Do you want to continue with this range?
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmLargeRange}
                className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
              >
                Yes, Fetch Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && !showConfirmDialog && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default DateRangePicker;
